import React, { useCallback, useEffect, useState } from 'react';
import './SearchConnector.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import Input from '../../../../Components/Common/Input/Input';
import MiniConnectorCard from '../../../../Components/MiniConnectorCard/MiniConnectorCard';
import { Pathname } from '../../../../Router/Pathname';
import { getConnectorById } from '../redux/action';
import { setStationDetail } from '../redux/SearchStationSlice';
import {
    resolveIsFavourite,
    setFavouriteOverride,
} from '../../FavouriteStationsView/redux/favouriteStationSlice';
import { useDispatch, useSelector } from 'react-redux';
import PreLoader from '../../../../Assets/icons/PreLoader/PreLoader';
import ConnectorHeader from './ConnectorHeader';
import {
    useFavouriteToggle,
    usePullToRefresh,
    useRefreshOnFocus,
} from '../../../../Hooks';
import { mapConnectorStatus } from '../../../../Utils';
const SearchConnector = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const { stations } = useSelector((store) => store);
    const { connectorDetails, loading, stationDetail } = stations;
    const { favouriteOverrides } = useSelector((store) => store.favouriteStations);
    const [searchPoint, setSearchPoint] = useState();

    const stationId = location?.state?.id;
    const isFavourite = resolveIsFavourite(favouriteOverrides, {
        id: stationDetail?.id || stationId,
        is_favourite: stationDetail?.is_favourite,
    });

    const handleChargingPointSearch = (value) => {
        setSearchPoint(value);
    };
    const handleNavigate = (connector) => {
        const isConnectorAvailable = connector?.connector_meta_cp_status === 'Available' ||
            connector?.connector_meta_cp_status === 'Preparing';
        if (!isConnectorAvailable) return;

        navigate(Pathname.ENERGY_BASED, {
            state: {
                connector_id: connector?.connector_meta_cp_id,
                charging_point_name: connector?.charging_point_name,
                io_type: connector?.connector_io_type,
            },
        });
    };

    const applyLocalFavourite = useCallback(
        (id, nextIsFavourite) => {
            dispatch(setFavouriteOverride({ id, is_favourite: nextIsFavourite }));
        },
        [dispatch]
    );

    const { toggleFavourite } = useFavouriteToggle({ applyLocal: applyLocalFavourite });

    const renderConnectorCard = () => {
        return connectorDetails?.map((connector) => (
            <MiniConnectorCard
                key={connector?.connector_meta_cp_id}
                name={connector?.charging_point_name}
                io_type={`${connector?.id}-${connector?.connector_meta_cp_name} ${connector?.connector_io_type}`}
                status={mapConnectorStatus(connector?.connector_meta_cp_status)}
                onClick={() => handleNavigate(connector)}
            />
        ));
    };

    const loadConnectors = useCallback(() => {
        if (!stationId) return undefined;
        return dispatch(
            getConnectorById({
                station_id: stationId,
                charging_point_name: searchPoint,
            })
        );
    }, [dispatch, stationId, searchPoint]);

    // Availability is live — refetch when the screen comes back into focus.
    useRefreshOnFocus(loadConnectors);
    const { pullProps, pullIndicator } = usePullToRefresh(loadConnectors);

    useEffect(() => {
        if (!location?.state?.id) return;
        dispatch(
            setStationDetail({
                id: location.state.id,
                station_name: location.state.name,
                address: location.state.address,
                is_favourite: Boolean(location.state.is_favourite),
            })
        );
    }, [location?.state?.id]);

    // `loadConnectors` covers both the station and the search text, so the
    // debounce re-arms if either changes — keying this on `searchPoint` alone
    // would keep showing the previous station's connectors.
    useEffect(() => {
        if (location?.state) {
            const debouncedStationName = setTimeout(() => {
                loadConnectors();
            }, 500);
            return () => clearTimeout(debouncedStationName);
        }
    }, [loadConnectors]);

    return (
        <main className="SearchConnector" {...pullProps}>
            <ConnectorHeader
                stationName={stationDetail?.station_name || location?.state?.name}
                isFavourite={isFavourite}
                onToggleFavourite={() =>
                    toggleFavourite(stationDetail?.id || stationId, isFavourite)
                }
                backTo={location?.state?.from}
            />
            <div className="SearchConnector__Input">
                <Input
                    label={'Search Connectors'}
                    onValueChange={handleChargingPointSearch}
                />
            </div>

            <div>
                {pullIndicator}
                <div className="SearchConnector__Cards">
                    {connectorDetails?.length > 0 ? (
                        renderConnectorCard()
                    ) : loading ? (
                        <div className="SearchConnector__Preloader">
                            <PreLoader />
                        </div>
                    ) : (
                        <h1>No connectors for this station</h1>
                    )}
                </div>
            </div>
        </main>
    );
};

export default SearchConnector;
